import { ReactComponent as ChevronRightIcon } from 'assett/icons/chevron_right_icon.svg';
import dayjs from 'dayjs';
import { openMembersDetail } from 'feature/billing/billingSlice';
import { handleChange } from 'feature/form/formSlice';
import { setSelectOne } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';

import {
	Checkbox,
	IconButton,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from 'styles';
import { IMemberAggregationBilling } from 'types/Billing';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { formatPrice } from 'utils/utils';

interface IMembersBillingAggregationTableBody {
	membersBilling: IMemberAggregationBilling[];
}

const MembersBillingAggregationTableBody = ({
	membersBilling,
}: IMembersBillingAggregationTableBody) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { selectedItems, downloadAll } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);
	const { sortingFields, sortingDirections } = useSelector(
		(store: RootState) => store.form,
	);

	const dispatch = useDispatch<AppDispatch>();

	const navigate = useNavigate();
	const handleClick = (el: IMemberAggregationBilling) => {
		const billingMonth = dayjs(el.billingMonth, 'YYYY-MM');

		// detail starts from page 1; the master saves its sort to restore it
		dispatch(
			openMembersDetail({ name: sortingFields, value: sortingDirections }),
		);

		dispatch(
			handleChange({
				name: 'inputMemberBillingMonthStart',
				value: billingMonth,
			}),
		);
		dispatch(
			handleChange({
				name: 'inputMemberBillingMonthEnd',
				value: billingMonth,
			}),
		);
		dispatch(handleChange({ name: 'inputMemberClient', value: [el.clientCode] }));
		dispatch(handleChange({ name: 'inputMemberGroups', value: [el.groupCode] }));
		dispatch(
			handleChange({ name: 'inputMemberPlanCodes', value: [el.planCode] }),
		);

		// Build URL query params for member filters derived from the clicked row.
		// These allow the /view-billing/members page to restore filters on a hard
		// refresh without needing Redux state to survive.
		const params = new URLSearchParams();

		const monthFormatted = billingMonth.format('YYYY-MM');
		params.set('memberMonthStart', monthFormatted);
		params.set('memberMonthEnd', monthFormatted);
		params.set('memberClient', el.clientCode);
		params.set('memberGroups', el.groupCode);
		params.set('memberPlanCodes', el.planCode);

		navigate(`/view-billing/members?${params.toString()}`);
	};

	return membersBilling.length > 0 ? (
		<TableBody>
			{membersBilling.map(el => (
				<TableRow
					key={el.id}
					sx={{
						'& > td': {

							padding: '5px 0px 5px 10px',
							'&:last-of-type': { minWidth: 'auto' },
							'&:first-of-type': { minWidth: 'auto' },
						},
					}}
				>
					<TableCell>
						<Checkbox
							label=""
							sx={{ p: 0, ml: 0 }}
							checked={
								downloadAll
									? !selectedItems.find(id => el.id === id)
									: !!selectedItems.find(id => el.id === id)
							}
							onClick={() => dispatch(setSelectOne(el.id))}
						/>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.carrierName || '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{el.billingMonth
								? dayjs(el.billingMonth, 'YYYY-MM').format('MMM YYYY')
								: '-'}
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.clientName || '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.groupName || '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.planDescription || '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.memberCount ?? '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{formatPrice(el.billingRate) ?? '-'}
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{formatPrice(el.totalAmount) ?? '-'}
						</Typography>
					</TableCell>
					<TableCell />
					<TableCell>
						<IconButton onClick={() => handleClick(el)}>
							<ChevronRightIcon />
						</IconButton>
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	) : (
		<TableBody
			sx={{
				'& td': {
					paddingLeft: '0px!important',
					textAlign: 'left!important',
					backgroundColor: 'transparent!important',
				},
			}}
		>
			<TableRow>
				<TableCell colSpan={20}>
					<Typography variant="bodyLink" sx={{ width: '100%' }}>
						{tClientLabels('commons.noResultsFound')}
					</Typography>
				</TableCell>
			</TableRow>
		</TableBody>
	);
};

export default MembersBillingAggregationTableBody;
